import {
	createActionsHook,
	createStateHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type HookStateFunction,
	type Store,
	type StoreActionApi,
} from 'react-sweet-state';

import type { CustomGreetingConfig, RovoConfiguration } from '../shared-types/rovoConfigTypes.tsx';
import type { ConversationAction } from '@atlassian/conversation-assistant';
import type { ConversationActionTags } from '@atlassian/rovo-conversation-actions/types';

export type RovoCurrentConfigState = {
	configuration: RovoConfiguration;
};

const noopConfiguration: RovoConfiguration = {
	getActions: async () => [],
};

const initialState: RovoCurrentConfigState = {
	configuration: noopConfiguration,
};

const actions = {
	setConfiguration:
		(configuration: RovoConfiguration) =>
		({ setState }: StoreActionApi<RovoCurrentConfigState>): void => {
			setState({ configuration });
		},
	resetConfiguration:
		() =>
		({ setState }: StoreActionApi<RovoCurrentConfigState>): void => {
			setState({ configuration: noopConfiguration });
		},
	updateConfiguration:
		(configuration: Partial<RovoConfiguration>) =>
		({ setState, getState }: StoreActionApi<RovoCurrentConfigState>): void => {
			const state = getState();
			setState({ configuration: { ...state.configuration, ...configuration } });
		},
};

export const RovoCurrentConfigStore: Store<
	RovoCurrentConfigState,
	{
		setConfiguration: (
			configuration: RovoConfiguration,
		) => ({ setState }: StoreActionApi<RovoCurrentConfigState>) => void;
		resetConfiguration: () => ({ setState }: StoreActionApi<RovoCurrentConfigState>) => void;
		updateConfiguration: (
			configuration: Partial<RovoConfiguration>,
		) => ({ setState, getState }: StoreActionApi<RovoCurrentConfigState>) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'RovoCurrentConfigStore',
});

export const useRovoCurrentConfigStoreActions: HookActionsFunction<
	BoundActions<
		RovoCurrentConfigState,
		{
			setConfiguration: (
				configuration: RovoConfiguration,
			) => ({ setState }: StoreActionApi<RovoCurrentConfigState>) => void;
			resetConfiguration: () => ({ setState }: StoreActionApi<RovoCurrentConfigState>) => void;
			updateConfiguration: (
				configuration: Partial<RovoConfiguration>,
			) => ({ setState, getState }: StoreActionApi<RovoCurrentConfigState>) => void;
		}
	>
> = createActionsHook(RovoCurrentConfigStore);

export const useRovoCurrentConfig: HookStateFunction<RovoConfiguration, void> = createStateHook(
	RovoCurrentConfigStore,
	{
		selector: (state: RovoCurrentConfigState) => state.configuration,
	},
);

// Property-specific selectors for optimized re-renders
export const useRovoConfigGetActions: HookStateFunction<
	(() => Promise<ConversationAction[]>) | undefined,
	void
> = createStateHook(RovoCurrentConfigStore, {
	selector: (state: RovoCurrentConfigState) => state.configuration.getActions,
});

export const useRovoConfigTags: HookStateFunction<ConversationActionTags | undefined, void> =
	createStateHook(RovoCurrentConfigStore, {
		selector: (state: RovoCurrentConfigState) => state.configuration.tags,
	});

export const useRovoConfigCustomGreeting: HookStateFunction<
	CustomGreetingConfig | undefined,
	void
> = createStateHook(RovoCurrentConfigStore, {
	selector: (state: RovoCurrentConfigState) => state.configuration.customGreeting,
});

export const useRovoConfigShouldShowGreeting: HookStateFunction<boolean | undefined, void> =
	createStateHook(RovoCurrentConfigStore, {
		selector: (state: RovoCurrentConfigState) => state.configuration.shouldShowGreeting,
	});

export const useRovoConfigPrioritizeCustomActionResults: HookStateFunction<
	boolean | undefined,
	void
> = createStateHook(RovoCurrentConfigStore, {
	selector: (state: RovoCurrentConfigState) => state.configuration.prioritizeCustomActionResults,
});

export const useRovoConfigNumStartersToShow: HookStateFunction<number | undefined, void> =
	createStateHook(RovoCurrentConfigStore, {
		selector: (state: RovoCurrentConfigState) => state.configuration.numStartersToShow,
	});
